/**
 * ApproveCategoryDialog — the category-create form shown when an admin
 * approves a `kind="category"` suggestion (PIM Wave P3 Task 4). A SIBLING
 * `BottomSheet` to `SuggestionDrawer`'s own sheet — never nested inside it
 * (see `attribute-fields/components/GroupManager.tsx`'s stacking note: a
 * `BottomSheet` nested as JSX-children of another open `BottomSheet` cannot
 * escape the parent's transformed panel).
 *
 * `slug` has NO server-side fallback for a category approval (unlike
 * name/parent, which fall back to the suggestion's submitted payload) — the
 * submit button stays disabled until a non-blank slug is entered. A
 * duplicate slug currently raises an unhandled 500 server-side (PIM Wave P3
 * Task 2 Minor finding — matches pre-existing `create_category` behavior,
 * not yet hardened to a clean 409), so any `ApiError` OTHER than the
 * documented `pim.suggestion_parent_invalid` 422 renders one generic
 * `--g-error` banner rather than switching on a structured code that does
 * not exist for that case.
 */
import { useEffect, useState, type ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { BottomSheet, Button, Field } from "@garden/ui";
import { TrilingualInput, type TrilingualValue } from "@/features/admin/_shared";
import { ADMIN_NS, registerAdminI18n } from "@/features/admin/api";
import { ApiError } from "@/lib/api-client";
import {
  categoryDisplayName,
  useCategoryTree,
  type Category,
} from "@/features/marketplace/useCategoryTree";
import { useApproveSuggestion } from "@/features/admin/suggestions/hooks";
import type { SuggestionResponse } from "@/features/admin/suggestions/api";

const SELECT_CLASS =
  "min-h-[44px] w-full rounded-[var(--g-radius-sm)] border border-[var(--g-hairline)] bg-[var(--g-surface)] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--g-primary)]";

interface FlatCategoryOption {
  id: string;
  label: string;
}

/**
 * Flattens the tree to indented options — mirrors `CategoryMultiPicker`'s
 * `categoryOptions` helper, minus the leaf-only restriction: any existing
 * category (leaf or branch) is a valid parent for a brand-new one.
 */
function flattenCategoryOptions(
  tree: readonly Category[],
  language: string,
  depth = 0,
): FlatCategoryOption[] {
  const out: FlatCategoryOption[] = [];
  for (const node of tree) {
    out.push({
      id: node.id,
      label: `${"  ".repeat(depth)}${categoryDisplayName(node, language)}`,
    });
    out.push(...flattenCategoryOptions(node.children, language, depth + 1));
  }
  return out;
}

export interface ApproveCategoryDialogProps {
  suggestion: SuggestionResponse;
  open: boolean;
  onClose: () => void;
  /** Called after a successful approve — the parent drawer closes too. */
  onApproved: () => void;
}

export function ApproveCategoryDialog({
  suggestion,
  open,
  onClose,
  onApproved,
}: ApproveCategoryDialogProps): ReactElement {
  registerAdminI18n();
  const { t, i18n } = useTranslation(ADMIN_NS);
  const { tree } = useCategoryTree();
  const approve = useApproveSuggestion();

  const [slug, setSlug] = useState("");
  const [name, setName] = useState<TrilingualValue>({ ru: "", ky: "", en: "" });
  const [parentId, setParentId] = useState("");
  const [parentError, setParentError] = useState<string | null>(null);
  const [genericError, setGenericError] = useState<string | null>(null);

  // Re-seed from the suggestion's payload every time the dialog OPENS (this
  // component is a long-lived sibling, not remounted per suggestion — the
  // parent `SuggestionDrawer` cycles `open` through false between two
  // different suggestions' approve sessions, so seeding on the false->true
  // edge is exactly "once per open" and never clobbers in-progress edits).
  useEffect(() => {
    if (!open) return;
    setSlug("");
    setName({ ru: suggestion.payload.name, ky: "", en: "" });
    setParentId(suggestion.payload.parent_category_id ?? "");
    setParentError(null);
    setGenericError(null);
  }, [open, suggestion]);

  const options = flattenCategoryOptions(tree, i18n.language);
  const slugValid = slug.trim().length > 0;

  const handleSubmit = async (): Promise<void> => {
    if (!slugValid) return;
    setParentError(null);
    setGenericError(null);
    try {
      await approve.mutateAsync({
        id: suggestion.id,
        body: {
          slug: slug.trim(),
          name_ru: name.ru.trim() || undefined,
          name_ky: name.ky.trim() || undefined,
          name_en: name.en.trim() || undefined,
          parent_id: parentId || undefined,
        },
      });
      toast.success(t("pim.suggestions.toast.approvedCategory"));
      onApproved();
    } catch (err) {
      if (err instanceof ApiError && err.problem?.code === "pim.suggestion_parent_invalid") {
        setParentError(t("pim.suggestions.approveCategory.errorParentInvalid"));
        return;
      }
      setGenericError(t("pim.suggestions.approveCategory.errorGeneric"));
    }
  };

  return (
    <BottomSheet open={open} onClose={onClose} title={t("pim.suggestions.approveCategory.title")}>
      <div className="flex flex-col gap-4">
        <Field
          label={t("pim.suggestions.approveCategory.slug")}
          data-testid="approve-category-slug"
          hint={t("pim.suggestions.approveCategory.slugHint")}
          value={slug}
          onChange={(e) => setSlug(e.target.value)}
        />

        <TrilingualInput
          id="approve-category-name"
          label={t("pim.suggestions.approveCategory.name")}
          value={name}
          onChange={setName}
          required
        />

        <div className="flex flex-col gap-1.5">
          <label
            htmlFor="approve-category-parent"
            className="text-[13px] font-semibold text-foreground"
          >
            {t("pim.suggestions.approveCategory.parent")}
          </label>
          <select
            id="approve-category-parent"
            data-testid="approve-category-parent"
            className={SELECT_CLASS}
            value={parentId}
            onChange={(e) => setParentId(e.target.value)}
          >
            <option value="">{t("pim.suggestions.approveCategory.noParent")}</option>
            {options.map((opt) => (
              <option key={opt.id} value={opt.id}>
                {opt.label}
              </option>
            ))}
          </select>
          {parentError ? (
            <p role="alert" className="text-xs text-[var(--g-error)]">
              {parentError}
            </p>
          ) : null}
        </div>

        {genericError ? (
          <div
            data-testid="approve-category-error"
            role="alert"
            className="rounded-[var(--g-radius)] border border-[var(--g-error)]/40 bg-[var(--g-error)]/10 p-3 text-sm text-[var(--g-error)]"
          >
            {genericError}
          </div>
        ) : null}

        <div className="flex justify-end gap-2">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onClose}
            disabled={approve.isPending}
            className="min-h-[44px]"
          >
            {t("common.cancel")}
          </Button>
          <Button
            type="button"
            variant="primary"
            size="sm"
            data-testid="approve-category-submit"
            loading={approve.isPending}
            disabled={!slugValid}
            onClick={() => void handleSubmit()}
            className="min-h-[44px]"
          >
            {t("pim.suggestions.approveCategory.submit")}
          </Button>
        </div>
      </div>
    </BottomSheet>
  );
}

export default ApproveCategoryDialog;
